"use client";

/**
 * `FormErrorBanner` — top-of-form callout for **form-level** validation
 * errors. These are the errors zod surfaces under the synthetic `root`
 * key when a `.refine()` (or a `.superRefine()` `addIssue` call without a
 * field path) fires — the canonical example is "password and confirm
 * must match" or "VAT amount must equal sum of line items". Per-field
 * errors continue to render under their own input via {@link FieldShell};
 * this banner only handles the cross-field ones.
 *
 * The banner is intentionally a tiny presentational primitive rather than
 * a full alert system. It accepts a single error message (the typical
 * shape of a zod refine result) and renders it inside a styled callout.
 * If a future use case needs multi-error banners (rare in practice — most
 * form-level refinements emit one issue), wrap the banner or pass a
 * formatted multi-line message.
 *
 * Pre-baked into the engine here per Plan UI-4 Task 15 even though the
 * `<Form>` composer that wires it to `formState.errors.root` lands in
 * Phase J Task 19. Putting the slot in place now means the composer wires
 * a single component rather than authoring its own callout markup, and
 * gives us a stable visual that the showcase + demo can match.
 *
 * Style: red border, red icon, dark fg-on-tinted-bg text. We use the
 * danger token for both the border and the accent rather than a bg-red
 * fill because:
 *
 * - A solid red bar at the top of a form reads as catastrophic — wrong
 *   register for a "two fields don't agree" error.
 * - The accent-bordered card matches the existing error treatment used in
 *   `EmptyState` and `ErrorState` from `@aio/platform-ui`'s states module.
 *
 * Accessibility: rendered with `role="alert"` so screen readers announce
 * it the moment it appears (the typical case is appearing post-submit).
 * The icon is `aria-hidden` since the message text already conveys the
 * meaning.
 */

import * as React from "react";
import { AlertCircle } from "lucide-react";
import { cn } from "@aio/platform-ui";

export interface FormErrorBannerProps {
  /**
   * The error message to display. When `undefined` or an empty string,
   * the banner renders `null` — callers can pass through
   * `formState.errors.root?.message` directly without an external
   * conditional.
   */
  message?: string;
  /** Optional caller-supplied class names for layout overrides. */
  className?: string;
}

export function FormErrorBanner({
  message,
  className,
}: FormErrorBannerProps): React.ReactElement | null {
  if (!message) return null;
  return (
    <div
      role="alert"
      data-testid="form-error-banner"
      className={cn(
        "flex items-start gap-2 rounded-md border border-danger bg-danger/5 p-3 text-sm text-fg",
        className,
      )}
    >
      <AlertCircle
        aria-hidden="true"
        className="mt-0.5 size-4 shrink-0 text-danger"
      />
      <p className="leading-relaxed">{message}</p>
    </div>
  );
}
